import { moment } from '@hotjar/insights-common/lib/moment';
import type {
  DateRange,
  RelativeDateRange,
} from '../../../components/StatelessComponent/SingleFilter/DateRangeFilter/DateRangeFilterType';
import {
  DATE_RANGE_TYPE,
  DATE_UNITS,
} from '../../../components/StatelessComponent/SingleFilter/DateRangeFilter/DateRangeFilterType';
import type { IntermediateFormat } from '../../../domains/global-filters/model/IntermediateFormat';
import type { QueryMapFilter } from '../../../domains/global-filters/model/QueryFormat';
import { QueryMapOperandEnum } from '../../../domains/global-filters/model/QueryFormat';

const shouldNotHappen = (never: never): void => {
  throw new Error(`Unknown type: ${never}`);
};

function getNumberOfDays(uiValue: RelativeDateRange): number {
  switch (uiValue.unit) {
    case DATE_UNITS.DAYS: {
      return +uiValue.amount;
    }
    case DATE_UNITS.HOURS: {
      if (uiValue.amount === 24) {
        // 24 hours is a special case:
        // If we send {"DAYS_AGO":{"created":1}}, it seems to consider now - (start of the previous day)
        // => that's not what we want
        // If we take {"DAYS_AGO":{"created":0}}, it seems to consider now - (24 hours) exactly
        return 0;
      }
      return Math.trunc(+uiValue.amount / 24);
    }
    case DATE_UNITS.MONTHS: {
      if (uiValue.amount === 12) {
        // A month is not exactly 30 days so we do a special case for one year:
        return 365;
      }
      return Math.trunc(+uiValue.amount * 30);
    }
    default:
      shouldNotHappen(uiValue.unit);
  }
  return 0;
}

export function fromUiToQueryFormat(uiValue: DateRange): QueryMapFilter {
  switch (uiValue.type) {
    case DATE_RANGE_TYPE.RELATIVE: {
      return {
        [QueryMapOperandEnum.DAYS_AGO]: {
          created: getNumberOfDays(uiValue),
        },
      };
    }
    case DATE_RANGE_TYPE.ABSOLUTE: {
      return {
        [QueryMapOperandEnum.RANGE_IN]: {
          created: [
            moment(uiValue.from).startOf('day').unix(),
            moment(uiValue.to).endOf('day').unix(),
          ],
        },
      };
    }
    case DATE_RANGE_TYPE.ALL: {
      // There is no specific format for DATE_RANGE_TYPE.ALL but this value will be treated as if filtering for all dates
      return {
        [QueryMapOperandEnum.DAYS_AGO]: {
          created: -1,
        },
      };
    }
    default:
      shouldNotHappen(uiValue);
  }
  throw new Error("We shouldn't get there.");
}

export function fromIntermediateToUiFormat(intermediateValue: IntermediateFormat): DateRange {
  if (intermediateValue.operation[0] === QueryMapOperandEnum.DAYS_AGO) {
    const value = +(intermediateValue.value[0] as number);
    if (value === -1) {
      return { type: DATE_RANGE_TYPE.ALL };
    }

    let amount: number;
    let unit: DATE_UNITS;

    if (value === 0) {
      // 0 is a special case:
      // If we send {"DAYS_AGO":{"created":1}}, it seems to consider now - (start of the previous day)
      // => that's not what we want
      // If we take {"DAYS_AGO":{"created":0}}, it seems to consider now - (24 hours) exactly
      amount = 24;
      unit = DATE_UNITS.HOURS;
    } else if (value <= 1) {
      amount = +value * 24;
      unit = DATE_UNITS.HOURS;
    } else if (value <= 30) {
      amount = +value;
      unit = DATE_UNITS.DAYS;
    } else if (value === 365) {
      // A month is not exactly 30 days so we do a special case for one year:
      amount = 12;
      unit = DATE_UNITS.MONTHS;
    } else {
      amount = Math.trunc(+value / 30);
      unit = DATE_UNITS.MONTHS;
    }
    return {
      type: DATE_RANGE_TYPE.RELATIVE,
      amount,
      unit,
    };
  }
  if (intermediateValue.operation[0] === QueryMapOperandEnum.RANGE_IN) {
    return {
      type: DATE_RANGE_TYPE.ABSOLUTE,
      from: new Date(+intermediateValue.value[0] * 1000),
      to: new Date(+intermediateValue.value[1] * 1000),
    };
  }
  // Return a default date
  return {
    type: DATE_RANGE_TYPE.RELATIVE,
    amount: 90,
    unit: DATE_UNITS.DAYS,
  };
}
